/**
 * src/pages/WhatIsConsciousness/sections/MemoryDemo.tsx
 * Working interactive Deese-Roediger-McDermott (DRM) false-memory experiment.
 * Displays sleep-related study words sequentially, tests recognition with shown words,
 * unshown distractors, and the critical non-presented lure ("sleep"), revealing reconstructive memory.
 * Completely local state, keyboard & touch accessible, reset anytime.
 */

import React, { useState, useEffect } from "react";
import { Play, RotateCcw, Check, X, HelpCircle, ArrowRight } from "lucide-react";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import "@/components/cosmic/cosmic.css";

// 12 related study list words (the critical lure "sleep" is deliberately NEVER shown)
const STUDY_WORDS = ["bed", "rest", "awake", "tired", "dream", "snore", "nap", "blanket", "doze", "slumber", "snooze", "yawn"];
interface QuizItem {
  word: string;
  type: "studied" | "lure" | "unrelated";
}
const QUIZ_ITEMS: QuizItem[] = [{
  word: "bed",
  type: "studied"
}, {
  word: "cloud",
  type: "unrelated"
}, {
  word: "sleep",
  type: "lure"
},
// The false memory lure!
{
  word: "dream",
  type: "studied"
}, {
  word: "guitar",
  type: "unrelated"
}, {
  word: "blanket",
  type: "studied"
}, {
  word: "apple",
  type: "unrelated"
}, {
  word: "snore",
  type: "studied"
}];
type Stage = "intro" | "studying" | "quiz" | "results";
export function MemoryDemo() {
  const [stage, setStage] = useState<Stage>("intro");
  const [currentWordIdx, setCurrentWordIdx] = useState<number>(0);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [autoAdvance, setAutoAdvance] = useState<boolean>(true);

  // Auto-advance study list words if toggled on
  useEffect(() => {
    if (stage !== "studying" || !autoAdvance) return;
    const timer = setTimeout(() => {
      if (currentWordIdx < STUDY_WORDS.length - 1) {
        setCurrentWordIdx(prev => prev + 1);
      } else {
        setStage("quiz");
      }
    }, 1200);
    return () => clearTimeout(timer);
  }, [stage, currentWordIdx, autoAdvance]);
  const handleStart = () => {
    setAnswers({});
    setCurrentWordIdx(0);
    setStage("studying");
  };
  const handleNextWordManual = () => {
    if (currentWordIdx < STUDY_WORDS.length - 1) {
      setCurrentWordIdx(prev => prev + 1);
    } else {
      setStage("quiz");
    }
  };
  const handleAnswer = (word: string, recognized: boolean) => {
    setAnswers(prev => ({
      ...prev,
      [word]: recognized
    }));
  };
  const handleFinishQuiz = () => {
    setStage("results");
  };
  const handleReset = () => {
    setStage("intro");
    setCurrentWordIdx(0);
    setAnswers({});
  };

  // Evaluation metrics
  const lureWord = "sleep";
  const lureFalselyRecognized = answers[lureWord] === true;
  const studiedWords = QUIZ_ITEMS.filter(q => q.type === "studied");
  const studiedHits = studiedWords.filter(q => answers[q.word] === true).length;
  const unrelatedWords = QUIZ_ITEMS.filter(q => q.type === "unrelated");
  const falseAlarmsUnrelated = unrelatedWords.filter(q => answers[q.word] === true).length;
  return <section className="w-full max-w-full min-w-0" aria-labelledby="memory-demo-heading">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span className="ql-eyebrow">Cognitive Self-Simulation</span>
          </div>
          <h2 id="memory-demo-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Memory & Reconstructive Selfhood
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <EvidenceBadge level="established" />
          <Footnote n={7} />
        </div>
      </div>

      <div className="ql-glass p-6 sm:p-8 w-full max-w-full min-w-0">
        {/* Intro Stage */}
        {stage === "intro" && <div className="flex flex-col items-center text-center max-w-2xl mx-auto py-6">
            <div className="size-12 rounded-full border border-primary/40 bg-primary/10 flex items-center justify-center text-primary mb-4 shadow-[0_0_16px_var(--glow-primary)]">
              <Play className="size-6 ml-0.5" />
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground mb-3">
              The DRM False Memory Experiment
            </h3>
            <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              Human memory is not a pristine video recorder; it is a generative, reconstructive simulation.
              In this brief 60-second cognitive experiment (Roediger & McDermott 1995), you will observe a sequence
              of 12 words, then test your memory against a brief recognition check.
            </p>
            <div className="flex items-center gap-4 flex-wrap justify-center">
              <button type="button" onClick={handleStart} className="ql-btn">
                <span>Begin Experiment</span>
              </button>
            </div>
          </div>}

        {/* Study Stage */}
        {stage === "studying" && <div className="flex flex-col items-center text-center max-w-xl mx-auto py-8">
            <div className="flex items-center justify-between w-full font-mono text-xs text-muted-foreground mb-8">
              <span>Encoding Phase</span>
              <span>
                Word {currentWordIdx + 1} of {STUDY_WORDS.length}
              </span>
            </div>

            <div className="h-32 flex items-center justify-center my-4">
              <span className="font-serif text-4xl sm:text-6xl font-extrabold tracking-wide text-foreground uppercase ql-title">
                {STUDY_WORDS[currentWordIdx]}
              </span>
            </div>

            <p className="text-xs font-mono text-muted-foreground mb-8">
              Focus on each word as it appears on screen.
            </p>

            <div className="flex items-center gap-4 flex-wrap justify-center">
              <button type="button" onClick={handleNextWordManual} className="ql-btn-ghost text-xs">
                <span>Next Word Now</span>
                <ArrowRight className="size-3.5" />
              </button>
              <button type="button" onClick={() => setAutoAdvance(!autoAdvance)} className="font-mono text-xs text-muted-foreground hover:text-primary transition-colors underline">
                {autoAdvance ? "Pause Auto-Advance" : "Resume Auto-Advance"}
              </button>
            </div>
          </div>}

        {/* Quiz Stage */}
        {stage === "quiz" && <div className="max-w-2xl mx-auto py-4">
            <div className="flex items-center justify-between font-mono text-xs text-muted-foreground border-b border-border/30 pb-3 mb-6">
              <span className="text-primary font-semibold">Recognition Check</span>
              <span>
                Answered: {Object.keys(answers).length} of {QUIZ_ITEMS.length}
              </span>
            </div>

            <p className="font-sans text-sm text-foreground mb-6">
              For each word below, indicate whether it was <strong>present in the study list</strong> you just observed:
            </p>

            <div className="space-y-3 mb-8">
              {QUIZ_ITEMS.map(item => {
            const currentAnswer = answers[item.word];
            return <div key={item.word} className="flex flex-col sm:flex-row sm:items-center justify-between p-3.5 rounded-lg border border-border/40 bg-card/40 gap-3">
                    <span className="font-serif text-base sm:text-lg font-bold text-foreground capitalize tracking-wide">
                      {item.word}
                    </span>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => handleAnswer(item.word, true)} className={`ql-chip ${currentAnswer === true ? "border-primary text-foreground" : ""}`} data-active={currentAnswer === true ? "true" : "false"} aria-pressed={currentAnswer === true}>
                        <Check className="size-3" />
                        <span>Was in list</span>
                      </button>
                      <button type="button" onClick={() => handleAnswer(item.word, false)} className={`ql-chip ${currentAnswer === false ? "border-primary text-foreground" : ""}`} data-active={currentAnswer === false ? "true" : "false"} aria-pressed={currentAnswer === false}>
                        <X className="size-3" />
                        <span>Was NOT in list</span>
                      </button>
                    </div>
                  </div>;
          })}
            </div>

            <div className="flex items-center justify-between gap-4 pt-4 border-t border-border/30">
              <button type="button" onClick={handleReset} className="ql-btn-ghost text-xs">
                <RotateCcw className="size-3.5" />
                <span>Restart</span>
              </button>
              <button type="button" disabled={Object.keys(answers).length < QUIZ_ITEMS.length} onClick={handleFinishQuiz} className="ql-btn">
                <span>Reveal Cognitive Findings</span>
              </button>
            </div>
          </div>}

        {/* Results Stage */}
        {stage === "results" && <div className="max-w-2xl mx-auto py-4 space-y-6">
            <div className="flex items-center justify-between border-b border-border/30 pb-3">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold">
                Experimental Findings
              </span>
              <button type="button" onClick={handleReset} className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-primary transition-colors">
                <RotateCcw className="size-3.5" />
                <span>Try Again</span>
              </button>
            </div>

            {/* Critical Lure Evaluation */}
            <div className={`p-5 rounded-xl border ${lureFalselyRecognized ? "border-destructive/40 bg-destructive/10" : "border-primary/40 bg-primary/10"}`}>
              <div className="flex items-center gap-2 mb-2 font-mono text-xs font-bold uppercase tracking-wider">
                <HelpCircle className="size-4 text-primary" />
                <span>Critical Non-Presented Lure: "SLEEP"</span>
              </div>
              <h4 className="font-serif text-lg font-bold text-foreground mb-2">
                {lureFalselyRecognized ? "You experienced the DRM False-Memory Effect!" : "You resisted the false memory lure!"}
              </h4>
              <p className="font-sans text-xs sm:text-sm text-foreground/90 leading-relaxed mb-3">
                {lureFalselyRecognized ? "You marked 'sleep' as having appeared in the study list. In reality, 'sleep' was never shown. Your brain activated its semantic network around bed, dream, tired, and snore, automatically synthesizing a vivid false memory of the central concept." : "You correctly rejected 'sleep', even though all 12 study words pointed semantically directly towards it. In classic laboratory settings, over 75% of participants falsely report having seen the lure word with high subjective confidence."}
              </p>
            </div>

            {/* Performance Summary Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-3.5 rounded-lg border border-border/40 bg-card/40">
                <span className="font-mono text-xs text-muted-foreground block mb-1">
                  Studied Words Recalled
                </span>
                <span className="font-serif text-xl font-bold text-foreground">
                  {studiedHits} / {studiedWords.length}
                </span>
              </div>
              <div className="p-3.5 rounded-lg border border-border/40 bg-card/40">
                <span className="font-mono text-xs text-muted-foreground block mb-1">
                  Critical Lure ("Sleep")
                </span>
                <span className={`font-serif text-xl font-bold ${lureFalselyRecognized ? "text-destructive" : "text-primary"}`}>
                  {lureFalselyRecognized ? "False Memory" : "Resisted"}
                </span>
              </div>
              <div className="p-3.5 rounded-lg border border-border/40 bg-card/40">
                <span className="font-mono text-xs text-muted-foreground block mb-1">
                  Unrelated False Alarms
                </span>
                <span className="font-serif text-xl font-bold text-foreground">
                  {falseAlarmsUnrelated} / {unrelatedWords.length}
                </span>
              </div>
            </div>

            {/* Philosophical & Scientific Conclusion */}
            <div className="p-4 rounded-lg bg-card/60 border border-border/40 text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
              <strong className="text-foreground">Reconstructive Selfhood:</strong> We experience our
              biographical identity as a solid continuum anchored by memories. Yet neurobiology shows that
              remembering is a generative reconstruction—an active simulation running in the present.
              Each retrieval alters synaptic weights, meaning you remember the last time you remembered, not the
              original event itself.
            </div>
          </div>}
      </div>
    </section>;
}